import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp, theme, Result, Button } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import AdminLayout from '@/layouts/AdminLayout';
import LoginPage from '@/pages/login';
import DashboardPage from '@/pages/dashboard';
import UsersPage from '@/pages/users';
import OrdersPage from '@/pages/orders';
import CreditsPage from '@/pages/credits';
import ScenesPage from '@/pages/scenes';
import SystemPage from '@/pages/system';
import FeedbackPage from '@/pages/feedback';
import InvitesPage from '@/pages/invites';
import BetaInvitesPage from '@/pages/beta-invites';
import ImagesPage from '@/pages/images';
import PromptServicesPage from '@/pages/prompt-services';
import UserLevelsPage from '@/pages/user-levels';
import UserBenefitsPage from '@/pages/user-benefits';
import { useAdminStore } from '@/stores/useAdminStore';
import api from '@/api/client';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = useAdminStore((s) => s.token);
  const admin = useAdminStore((s) => s.admin);
  const setAuth = useAdminStore((s) => s.setAuth);

  useEffect(() => {
    if (token && admin && !admin.permissions) {
      api.get('/auth/me').then((res) => {
        setAuth(token, res.data);
      }).catch(() => {});
    }
  }, [token, admin, setAuth]);

  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function PermissionRoute({ permission, children }: { permission: string; children: React.ReactNode }) {
  const hasPermission = useAdminStore((s) => s.hasPermission);
  if (!hasPermission(permission)) {
    return (
      <Result
        status="403"
        title="403"
        subTitle="抱歉，您没有权限访问此页面"
        extra={<Button type="primary" onClick={() => window.history.back()}>返回上一页</Button>}
      />
    );
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: { colorPrimary: '#1677ff', borderRadius: 6 },
      }}
    >
      <AntApp>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<DashboardPage />} />
              <Route path="/users" element={<PermissionRoute permission="users:view"><UsersPage /></PermissionRoute>} />
              <Route path="/orders" element={<PermissionRoute permission="orders:view"><OrdersPage /></PermissionRoute>} />
              <Route path="/credits" element={<PermissionRoute permission="credits:view"><CreditsPage /></PermissionRoute>} />
              <Route path="/scenes" element={<PermissionRoute permission="scenes:view"><ScenesPage /></PermissionRoute>} />
              <Route path="/invites" element={<InvitesPage />} />
              <Route path="/beta-invites" element={<BetaInvitesPage />} />
              <Route path="/images" element={<PermissionRoute permission="users:view"><ImagesPage /></PermissionRoute>} />
              <Route path="/user-levels" element={<PermissionRoute permission="system:view"><UserLevelsPage /></PermissionRoute>} />
              <Route path="/user-benefits" element={<PermissionRoute permission="system:view"><UserBenefitsPage /></PermissionRoute>} />
              <Route path="/prompt-services" element={<PermissionRoute permission="system:view"><PromptServicesPage /></PermissionRoute>} />
              <Route path="/feedback" element={<PermissionRoute permission="system:view"><FeedbackPage /></PermissionRoute>} />
              <Route path="/system" element={<PermissionRoute permission="system:view"><SystemPage /></PermissionRoute>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AntApp>
    </ConfigProvider>
  );
}
